import { useCallback, useEffect, useRef } from 'react';
import { ITEM_TYPE, useSearch } from 'core/contexts/search';
import { useBookingWizard } from 'core/contexts/bookingWizard';
import { Box, CircularProgress, Grid2, Typography } from '@mui/material';
import ActivityCard from '../ActivityCard';
import BundleCard from '../BundleCard';

const ActivityCards = ({ handleOpen }) => {
    const { contractId } = useBookingWizard();
    const { experiences, loading, search, searchFilterDebounce, experienceCursor, setExperienceCursor, bundleCursor, setBundleCursor } = useSearch();
    const sentinelRef = useRef(null);
    const observerRef = useRef(null);

    const loadData = useCallback(async () => {
        if (!experienceCursor && !bundleCursor) return;

        const result = await search(contractId, experienceCursor, bundleCursor);

        if (!result) return;

        if (result?.experienceCursor) {
            setExperienceCursor(result.experienceCursor);
        } else {
            setExperienceCursor(null);
        }

        if (result?.bundleCursor) {
            setBundleCursor(result.bundleCursor);
        } else {
            setBundleCursor(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contractId, experienceCursor, bundleCursor]);

    useEffect(() => {
        if (!sentinelRef.current) return;

        observerRef.current = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;

                if (entry.isIntersecting) {
                    loadData();
                }
            },
            {
                root: null,
                rootMargin: '32px',
                threshold: 0,
            }
        );

        observerRef.current.observe(sentinelRef.current);

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [loadData]);

    return (
        <Grid2 container size={12} spacing={2} sx={{ flex: 1, overflowY: 'auto' }}>
            {(searchFilterDebounce || contractId) && !loading && experiences?.length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <Typography>{'No se han encontrado experiencias o paquetes'}</Typography>
                </Box>
            )}

            {!searchFilterDebounce && !contractId && !loading && experiences?.length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <Typography>{'No tienes ninguna experiencia para hacer reservas'}</Typography>
                </Box>
            )}

            {experiences.map((experience) => {
                if (experience?.type === ITEM_TYPE.EXPERIENCE) {
                    return <ActivityCard key={`${experience?.id}-${experience?.type}`} {...experience} handleOpen={() => handleOpen(experience)} />;
                }

                if (experience?.type === ITEM_TYPE.BUNDLE) {
                    return <BundleCard key={`${experience?.id}-${experience?.type}`} {...experience} handleOpen={() => handleOpen(experience)} />;
                }

                return null;
            })}

            <div ref={sentinelRef} style={{ height: 1 }} />

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <CircularProgress />
                </Box>
            )}

            {!experienceCursor && !bundleCursor && !loading && experiences?.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <Typography>{'No hay más experiencias...'}</Typography>
                </Box>
            )}
        </Grid2>
    );
};

export default ActivityCards;
